import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, PlayCircle, MapPin } from "lucide-react";
import { STORE_ADDRESS, STORE_PHONE, STORE_PHONE_TEL, STORE_HOURS } from "@/constants/store";
import KineticText from "@/components/KineticText";

const HERO_VIDEO = "/media/hero-loop.mp4";
const HERO_POSTER = "/media/hero-poster.jpg";

const PARTICLES = [
  { left: "6%", size: 5, dur: 11, delay: 0 },
  { left: "14%", size: 3, dur: 14, delay: 2.2 },
  { left: "24%", size: 6, dur: 10, delay: 4.5 },
  { left: "33%", size: 4, dur: 13, delay: 1.4 },
  { left: "45%", size: 3, dur: 15, delay: 6 },
  { left: "56%", size: 5, dur: 12, delay: 3.1 },
  { left: "66%", size: 4, dur: 16, delay: 0.8 },
  { left: "74%", size: 6, dur: 11, delay: 5.3 },
  { left: "84%", size: 3, dur: 14, delay: 2.8 },
  { left: "92%", size: 5, dur: 12, delay: 7 },
];

export default function VideoHero() {
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  }, [videoFailed]);

  return (
    <section
      className="relative overflow-hidden bg-[#071330] text-white min-h-[540px] lg:min-h-[620px] flex items-center"
      data-testid="video-hero"
    >
      {/* Motion guarantee: Ken Burns pan/zoom photo always underneath; video fades in over it once playing */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={HERO_POSTER}
          alt="IPVALUE showroom"
          className="w-full h-full object-cover ken-burns"
          data-testid="hero-kenburns-img"
        />
        {!videoFailed && (
          <video
            ref={videoRef}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${videoPlaying ? "opacity-100" : "opacity-0"}`}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onPlaying={() => setVideoPlaying(true)}
            data-testid="hero-video-el"
          >
            <source src={HERO_VIDEO} type="video/mp4" />
            <source src="/media/hero-loop.webm" type="video/webm" onError={() => setVideoFailed(true)} />
          </video>
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-[#071330]/95 via-[#0A1E5E]/80 to-[#071330]/30" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#071330] to-transparent" />
      </div>

      {/* Gold shimmer particles */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        {PARTICLES.map((p, i) => (
          <span
            key={i}
            className="gold-particle"
            style={{
              left: p.left,
              width: p.size,
              height: p.size,
              animationDuration: `${p.dur}s`,
              animationDelay: `${p.delay}s`,
            }}
          />
        ))}
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-8 py-16 lg:py-24 w-full">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 border border-[#F0B90B]/50 bg-[#0A1E5E]/60 backdrop-blur-md text-[#F0B90B] px-3.5 py-1.5 rounded-full text-[11px] font-black tracking-[0.22em] uppercase animate-fade-up">
            <PlayCircle className="w-3.5 h-3.5" /> Grand Rapids, Michigan · Showroom + Online
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 leading-[1.02]">
            <KineticText text="Wholesale prices." delay={0.15} /><br />
            <span className="gold-shimmer-text">Everything under one roof.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-100/85 leading-relaxed max-w-lg animate-fade-up" style={{ animationDelay: "160ms" }}>
            Closeout, overstock &amp; liquidation inventory in our 11,000 sqft showroom —
            now shoppable online. Pick up in-store or get it delivered. FREE delivery
            on orders $299+ within 10 miles.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3 animate-fade-up" style={{ animationDelay: "240ms" }}>
            <Link
              to="/shop"
              className="cta-sweep inline-flex items-center gap-2 rounded-full bg-[#F0B90B] hover:bg-[#FFC220] text-[#0A1E5E] font-black px-8 py-3.5 shadow-[0_0_22px_rgba(240,185,11,0.45)] hover:shadow-[0_0_34px_rgba(240,185,11,0.7)] hover:-translate-y-0.5 transition-[background-color,box-shadow,transform]"
              data-testid="hero-shop-btn"
            >
              Shop Now <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/shop/Closeout & Overstock Pallets"
              className="inline-flex items-center gap-2 rounded-full border-2 border-white/80 text-white font-bold px-7 py-3 hover:bg-white hover:text-[#0A1E5E] hover:-translate-y-0.5 hover:shadow-[0_0_22px_rgba(255,255,255,0.35)] transition-[background-color,color,transform,box-shadow]"
              data-testid="hero-pallets-btn"
            >
              Wholesale Pallets
            </Link>
            <a
              href={`tel:${STORE_PHONE_TEL}`}
              className="inline-flex items-center gap-2 rounded-full border border-[#F0B90B]/60 bg-[#0A1E5E]/50 backdrop-blur-md text-[#F0B90B] font-bold px-6 py-3 hover:bg-[#F0B90B] hover:text-[#0A1E5E] hover:-translate-y-0.5 hover:shadow-[0_0_26px_rgba(240,185,11,0.55)] transition-[background-color,color,transform,box-shadow] [touch-action:manipulation]"
              data-testid="hero-call-btn"
            >
              <Phone className="w-4 h-4" /> {STORE_PHONE}
            </a>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-200/70 font-semibold animate-fade-up" style={{ animationDelay: "320ms" }}>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-[#F0B90B]" /> {STORE_ADDRESS.short}
            </span>
            <span className="hidden sm:inline text-[#F0B90B]/50">•</span>
            <span>{STORE_HOURS.display}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
